import React from 'react'
import gql from 'graphql-tag'
import * as yup from 'yup'
import {
	Form,
	TextField,
	Button,
	View,
	Link,
	Spinner,
	PageFooter,
	Icon,
} from '#hh/client/components'
import { useForm, useTranslations, translateString } from '#hh/client/hooks'
import HHLogo from '#hh/static/public/headlinehunter-03.svg'

export const ForgotPasswordMutation = gql`
	mutation ForgotPassword($email: String!, $locale: String) {
		forgotPassword(email: $email, locale: $locale) {
			success
			message
			errors {
				field
				message
			}
		}
	}
`

export function ForgotPasswordView() {
	const { messages, locale } = useTranslations()
	const [submitted, setSubmitted] = React.useState(false)
	const [shouldAnimate, setShouldAnimate] = React.useState(false)
	const isInitialMount = React.useRef(true)

	React.useEffect(() => {
		if (isInitialMount.current) {
			isInitialMount.current = false
		} else {
			setShouldAnimate(true)
		}
	}, [submitted])

	const validationSchema = yup.object().shape({
		email: yup
			.string()
			.email(messages.invalid_email)
			.required(
				translateString('required_field', messages, { field: messages.login_email }),
			)
			.label(messages.login_email),
	})

	const form = useForm({
		schema: validationSchema,
		mutation: ForgotPasswordMutation,
		mutationErrorsPath: 'forgotPassword.errors',
		mutationDataPath: 'forgotPassword',
		createSubmissionVariables: (values: any) => ({
			...values,
			locale,
		}),
		onSubmissionResponse: async ({ data, errors }) => {
			if (errors) {
				console.error(errors)
			}

			if (data) {
				form.clearErrors()
				setSubmitted(true)
			}
		},
	})

	const isLoading = form.isLoading || form.isSubmitSuccessful

	return (
		<View
			title={submitted ? messages.forgot_password_success_title : messages.forgot_password_title}
			className="view--pwreset view--page"
		>
			<main className="viewMain">
				<div className="pwreset floatyBox">
					<HHLogo className="pwreset__logo" />

					{/* Title + Description section - animated */}
					<div className={`pwreset__headerSection ${shouldAnimate ? 'pwreset__headerSection--mounted' : ''}`}>
						<div className="pwreset__headerWrapper">
							<div className={`pwreset__header ${submitted ? 'pwreset__header--hidden' : ''}`}>
								<h1 className="pwreset__title">{messages.forgot_password_title}</h1>
								<p className="pwreset__description">
									{messages.forgot_password_description}
								</p>
							</div>
							<div className={`pwreset__header ${submitted ? '' : 'pwreset__header--hidden'}`}>
								<h1 className="pwreset__title">{messages.forgot_password_success_title}</h1>
								<p className="pwreset__description">
									{messages.forgot_password_success_description}
								</p>
							</div>
						</div>
					</div>

					{/* Content section - animated */}
					<div className={`pwreset__contentSection ${shouldAnimate ? 'pwreset__contentSection--mounted' : ''}`}>
						<div className="pwreset__contentWrapper">
							<div className={`pwreset__content ${submitted ? 'pwreset__content--hidden' : ''}`}>
								<Form {...form} className="pwreset__form">
									<TextField
										name="email"
										label={messages.login_email}
										autoComplete="username"
									/>
									<Button
										type="submit"
										className="button pwreset__submit"
										icon={isLoading ? '' : 'forward_to_inbox'}
										loading={isLoading}
									>
										{messages.forgot_password_submit}
									</Button>
								</Form>
							</div>

							<div className={`pwreset__content ${submitted ? '' : 'pwreset__content--hidden'}`}>
								<Icon name="forward_to_inbox" className="pwreset__illu" />
							</div>
						</div>
					</div>

					{/* Footer section - animated */}
					<div className={`pwreset__footerSection ${shouldAnimate ? 'pwreset__footerSection--mounted' : ''}`}>
						<div className="pwreset__footerWrapper">
							<div className={`pwreset__footer ${submitted ? 'pwreset__footer--hidden' : ''}`}>
								<Link href="/login" className="pwreset__back">
									← {messages.forgot_password_back_to_login}
								</Link>
							</div>
							<div className={`pwreset__footer ${submitted ? '' : 'pwreset__footer--hidden'}`}>
								<Link href="/login" className="button pwreset__success floatyBox__action icon icon:arrow_back">
									{messages.forgot_password_success_button}
								</Link>
							</div>
						</div>
					</div>
				</div>
			</main>
			<PageFooter />
		</View>
	)
}
